// CompletionOverlay.jsx
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const statDelay = 0.4;

const fixedParticles = [
  { top: '10%', left: '15%', emoji: '✨' },
  { top: '25%', left: '75%', emoji: '🌟' },
  { top: '60%', left: '10%', emoji: '💫' },
  { top: '40%', left: '80%', emoji: '✨' },
  { top: '75%', left: '50%', emoji: '🌟' },
  { top: '15%', left: '60%', emoji: '💫' },
];

export default function CompletionOverlay({ playerName = "Explorer", score = 0, stats = {}, onComplete }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let current = 0;
    const increment = Math.max(1, Math.floor(score / 60));
    const interval = setInterval(() => {
      current += increment;
      if (current >= score) {
        setCount(score);
        clearInterval(interval);
      } else {
        setCount(current);
      }
    }, 20);

    const finishTimer = setTimeout(() => {
      onComplete?.();
    }, 3200);

    return () => {
      clearInterval(interval);
      clearTimeout(finishTimer);
    };
  }, [score, onComplete]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[60] bg-gradient-to-b from-indigo-900 via-purple-800 to-pink-700 flex flex-col items-center justify-center text-white overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.h2
          className="text-3xl sm:text-4xl font-bold mb-2 drop-shadow"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          🎉 Woohoo, {playerName}!
        </motion.h2>

        <motion.img
          src="/M-logo.png"
          alt="MazeMoh Logo"
          className="w-20 h-20 mb-4 drop-shadow-lg"
          initial={{ scale: 0, rotate: -10 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        />

        <motion.p
          className="text-lg mb-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          Analyzing your performance...
        </motion.p>

        <motion.div
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm text-center mb-4"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.2 } } }}
        >
          {[
            { label: "Gems", value: stats.gemsCollected || 0 },
            { label: "Time", value: stats.timeTaken + "s" },
            { label: "Steps", value: stats.playerPathLen },
            { label: "Difficulty", value: stats.difficulty || "Medium" },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="bg-white/10 p-2 rounded-lg"
              variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
            >
              <div className="font-bold text-xl">{item.value}</div>
              <div className="text-xs text-white/80">{item.label}</div>
            </motion.div>
          ))}
        </motion.div>

        <motion.h3
          className="text-xl text-yellow-300 font-semibold mb-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          ⭐ Final Score: {count.toLocaleString()}
        </motion.h3>

        <div className="relative w-64 h-3 bg-white/20 rounded-full overflow-hidden mb-6">
          <motion.div
            className="absolute top-0 left-0 h-full bg-yellow-400"
            initial={{ width: 0 }}
            animate={{ width: "100%" }}
            transition={{ duration: 2.8, ease: "easeInOut" }}
          />
        </div>

        {fixedParticles.map((p, i) => (
          <motion.div
            key={i}
            className="absolute pointer-events-none select-none"
            style={{
              top: p.top,
              left: p.left,
              fontSize: '1.5rem',
              opacity: 0.6,
              zIndex: 10,
            }}
            animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
            transition={{
              duration: 6,
              repeat: Infinity,
              repeatType: "loop",
              ease: "easeInOut",
              delay: Math.random(),
            }}
          >
            {p.emoji}
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  );
}